/** Brand visuals: geometric + subtle biomimicry, navy/orange — not soft “green eco”. */

import type { ReactNode } from "react";

type IconProps = { className?: string; title?: string };

export function SpiralMark({ className, title = "Biomimicry mark" }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 120 120" fill="none" aria-hidden={title ? undefined : true} role="img">
      {title ? <title>{title}</title> : null}
      <rect width="120" height="120" rx="28" fill="#0B1426" />
      <path
        d="M60 22c18 0 32 12 32 28s-10 24-24 28c-10 3-18 10-18 20 0 12 12 20 28 18"
        stroke="#FF6B00"
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M42 88c-12-4-18-14-16-26 3-16 18-26 34-26"
        stroke="#FF8533"
        strokeWidth="3"
        strokeLinecap="round"
        opacity="0.55"
        fill="none"
      />
      <circle cx="78" cy="40" r="4" fill="#FFB570" />
      <path d="M28 96h64" stroke="#FF6B00" strokeWidth="2" opacity="0.35" strokeLinecap="round" />
    </svg>
  );
}

export function HeroLandscape({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 640 480" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="sky" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#132038" />
          <stop offset="55%" stopColor="#0B1426" />
          <stop offset="100%" stopColor="#060D18" />
        </linearGradient>
        <linearGradient id="glow" x1="0.5" y1="0" x2="0.5" y2="1">
          <stop offset="0%" stopColor="#FF6B00" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#FF6B00" stopOpacity="0" />
        </linearGradient>
        <pattern id="grid" width="28" height="28" patternUnits="userSpaceOnUse">
          <path d="M28 0H0V28" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="640" height="480" rx="28" fill="url(#sky)" />
      <rect width="640" height="480" rx="28" fill="url(#grid)" />
      <circle cx="520" cy="90" r="90" fill="url(#glow)" />
      {/* Fibonacci-ish arcs */}
      <path d="M80 360c40-120 120-180 240-180s180 80 200 180" stroke="#FF6B00" strokeWidth="2" opacity="0.35" />
      <path d="M120 360c30-90 90-140 180-140s140 60 160 140" stroke="#FF8533" strokeWidth="2" opacity="0.25" />
      {/* Terrain */}
      <path
        d="M0 360c60-20 100-50 160-48 70 2 90 40 150 38 70-2 90-42 160-40 50 1 80 28 110 40v90H0V360z"
        fill="#132038"
      />
      <path
        d="M0 400c80-18 120-40 190-36 80 4 100 34 170 32 75-2 95-36 170-32 40 2 70 20 110 36v60H0v-60z"
        fill="#1C2D4A"
      />
      {/* Survey markers / engineering points */}
      <g stroke="#FF6B00" strokeWidth="2">
        <path d="M180 250v40M160 270h40" />
        <circle cx="180" cy="250" r="5" fill="#0B1426" />
        <path d="M380 210v36M364 228h32" />
        <circle cx="380" cy="210" r="5" fill="#0B1426" />
        <path d="M470 280v30M456 295h28" />
        <circle cx="470" cy="280" r="4" fill="#0B1426" />
      </g>
      {/* Contour lines */}
      <path d="M40 330c50 10 90-10 140-8s90 18 140 8 90-20 140-10 80 18 120 10" stroke="rgba(255,107,0,0.25)" strokeWidth="1.5" />
      <path d="M60 345c45 8 85-8 130-6s85 14 130 6 85-16 130-8 75 14 110 8" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" />
      {/* Structure silhouette */}
      <rect x="250" y="275" width="18" height="70" rx="2" fill="#060D18" opacity="0.85" />
      <rect x="275" y="255" width="22" height="90" rx="2" fill="#060D18" opacity="0.9" />
      <rect x="305" y="290" width="16" height="55" rx="2" fill="#060D18" opacity="0.8" />
      <text x="40" y="56" fill="#FF6B00" fontFamily="Space Grotesk, Inter, sans-serif" fontSize="14" fontWeight="700">
        SITE CONTEXT
      </text>
      <text x="40" y="78" fill="rgba(255,255,255,0.55)" fontFamily="Inter, sans-serif" fontSize="12">
        Contours · soils · controls · pathway
      </text>
    </svg>
  );
}

const audiencePaths: Record<string, ReactNode> = {
  architects: (
    <>
      <path d="M20 52 L40 18 L60 52 Z" stroke="currentColor" strokeWidth="2.2" fill="none" />
      <path d="M28 52 V62 H52 V52" stroke="currentColor" strokeWidth="2.2" fill="none" />
      <path d="M34 38 h12" stroke="currentColor" strokeWidth="2" />
      <circle cx="40" cy="28" r="2.5" fill="currentColor" />
    </>
  ),
  engineers: (
    <>
      <circle cx="40" cy="40" r="14" stroke="currentColor" strokeWidth="2.2" fill="none" />
      <path d="M40 22 V18 M40 62 V58 M22 40 H18 M62 40 H58" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M30 30 L26 26 M50 50 L54 54 M50 30 L54 26 M30 50 L26 54" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="40" cy="40" r="4" fill="currentColor" />
    </>
  ),
  developers: (
    <>
      <rect x="18" y="34" width="16" height="28" rx="2" stroke="currentColor" strokeWidth="2.2" fill="none" />
      <rect x="38" y="22" width="18" height="40" rx="2" stroke="currentColor" strokeWidth="2.2" fill="none" />
      <rect x="24" y="42" width="4" height="6" fill="currentColor" opacity="0.7" />
      <rect x="44" y="30" width="4" height="6" fill="currentColor" opacity="0.7" />
      <rect x="52" y="30" width="4" height="6" fill="currentColor" opacity="0.7" />
      <path d="M18 66 H62" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </>
  ),
  "local-gov": (
    <>
      <path d="M20 30 H60 V58 H20 Z" stroke="currentColor" strokeWidth="2.2" fill="none" />
      <path d="M28 30 V22 H52 V30" stroke="currentColor" strokeWidth="2.2" fill="none" />
      <path d="M32 40 H48 M32 48 H44" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="40" cy="18" r="3" fill="currentColor" />
    </>
  ),
  mining: (
    <>
      <path d="M18 58 L32 28 L48 48 L62 24 V62 H18 Z" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinejoin="round" />
      <path d="M26 58 L34 42 L42 52 L50 36" stroke="currentColor" strokeWidth="2" opacity="0.6" />
      <circle cx="56" cy="30" r="3" fill="currentColor" />
    </>
  ),
  energy: (
    <>
      <path d="M40 16 L28 40 H38 L32 64 L54 36 H42 L48 16 Z" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinejoin="round" />
      <path d="M18 66 H62" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
    </>
  ),
  contractors: (
    <>
      <path d="M22 50 H58 L52 62 H28 Z" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinejoin="round" />
      <path d="M30 50 V34 H50 V50" stroke="currentColor" strokeWidth="2.2" fill="none" />
      <path d="M36 34 V28 H44 V34" stroke="currentColor" strokeWidth="2.2" fill="none" />
      <path d="M26 42 H20 M60 42 H54" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </>
  ),
};

export function AudienceIcon({
  id,
  className,
}: {
  id: string;
  className?: string;
}) {
  return (
    <svg className={className} viewBox="0 0 80 80" fill="none" aria-hidden="true">
      <rect width="80" height="80" rx="18" fill="currentColor" opacity="0.1" />
      <g transform="translate(0,0)" color="currentColor">
        {audiencePaths[id] ?? audiencePaths.engineers}
      </g>
    </svg>
  );
}

export function ServiceGlyph({ name }: { name: string }) {
  const key = name.toLowerCase();
  let body = (
    <circle cx="20" cy="20" r="8" stroke="currentColor" strokeWidth="2" fill="none" />
  );
  if (key.includes("acid") || key.includes("ass")) {
    body = (
      <>
        <path d="M10 28 L20 8 L30 28 Z" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M20 16 V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="20" cy="25" r="1.2" fill="currentColor" />
      </>
    );
  } else if (key.includes("erosion") || key.includes("esc")) {
    body = (
      <>
        <path d="M6 26c6-8 10-8 14 0s8 8 14 0" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M8 14 H32" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M12 10 V14 M20 8 V14 M28 11 V14" stroke="currentColor" strokeWidth="2" />
      </>
    );
  } else if (key.includes("marine")) {
    body = (
      <>
        <path d="M6 20c4-6 8-6 12 0s8 6 12 0 8-6 12 0" stroke="currentColor" strokeWidth="2" fill="none" />
        <circle cx="20" cy="12" r="3" fill="currentColor" opacity="0.7" />
      </>
    );
  } else if (key.includes("soil")) {
    body = (
      <>
        <path d="M8 12 H32 V28 H8 Z" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M8 18 H32 M8 23 H32" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
      </>
    );
  } else if (key.includes("blue")) {
    body = (
      <>
        <circle cx="20" cy="18" r="8" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M14 18h12 M20 12v12" stroke="currentColor" strokeWidth="2" />
      </>
    );
  } else if (key.includes("wsud") || key.includes("water")) {
    body = (
      <>
        <path d="M20 8c8 10 10 14 10 18a10 10 0 1 1-20 0c0-4 2-8 10-18z" stroke="currentColor" strokeWidth="2" fill="none" />
      </>
    );
  } else if (
    key.includes("regulat") ||
    key.includes("approv") ||
    key.includes("due diligence") ||
    key.includes("authority") ||
    key.includes("mining")
  ) {
    body = (
      <>
        <rect x="10" y="8" width="20" height="24" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M14 14h12 M14 20h10 M14 26h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </>
    );
  } else if (key.includes("urban")) {
    body = (
      <>
        <rect x="8" y="16" width="10" height="14" stroke="currentColor" strokeWidth="2" fill="none" />
        <rect x="20" y="10" width="12" height="20" stroke="currentColor" strokeWidth="2" fill="none" />
      </>
    );
  } else if (
    key.includes("plant") ||
    key.includes("animal") ||
    key.includes("species") ||
    key.includes("flora") ||
    key.includes("fauna") ||
    key.includes("ecological") ||
    key.includes("protected") ||
    key.includes("pre-clearance") ||
    key.includes("habitat")
  ) {
    body = (
      <>
        <path d="M20 28 V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path
          d="M20 16c-6-2-8-8-6-12 4 2 6 6 6 12 0-6 2-10 6-12 2 4 0 10-6 12z"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
        />
        <circle cx="28" cy="24" r="3" stroke="currentColor" strokeWidth="1.8" fill="none" />
      </>
    );
  } else if (key.includes("offset")) {
    body = (
      <>
        <rect x="8" y="10" width="12" height="20" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
        <rect x="20" y="14" width="12" height="16" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M14 20h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </>
    );
  } else if (key.includes("pmav") || key.includes("vegetation") || key.includes("rehab")) {
    body = (
      <>
        <path d="M8 28 L14 12 L20 28 Z" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M20 28 L26 10 L32 28 Z" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M6 28 H34" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </>
    );
  } else if (key.includes("bushfire") || key.includes("bhmp")) {
    body = (
      <>
        <path
          d="M20 30c6 0 10-4 10-10 0-6-4-8-6-12-1 4-4 6-4 10 0-3-2-6-4-8-2 4-6 7-6 12 0 5 4 8 10 8z"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
        />
      </>
    );
  } else if (
    key.includes("construction") ||
    key.includes("cemp") ||
    key.includes("site-based") ||
    key.includes("sbmp") ||
    key.includes("weed")
  ) {
    body = (
      <>
        <path d="M10 28 V14 l10-4 10 4 V28" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M16 28 V20 H24 V28" stroke="currentColor" strokeWidth="2" fill="none" />
      </>
    );
  } else if (
    key.includes("performance metric") ||
    key.includes("energy monitor") ||
    key.includes("energy modell") ||
    key.includes("energy m&o") ||
    key.includes("m&v") ||
    key.includes("measurement") ||
    key.includes("green mark") ||
    key.includes("bms") ||
    key.includes("metering") ||
    key.includes("chiller") ||
    key.includes("acmv") ||
    key.includes("hvac") ||
    key.includes("electrical") ||
    key.includes("plumbing") ||
    key.includes("solar") ||
    key.includes("o&m") ||
    key.includes("retro-commission") ||
    key.includes("indoor environmental") ||
    key.includes("thermal comfort") ||
    key.includes("mep")
  ) {
    body = (
      <>
        <rect x="9" y="10" width="22" height="16" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M13 22 V16 M18 22 V14 M23 22 V17 M28 22 V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M14 28 H26" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    );
  } else if (key.includes("monitoring") || key.includes("compliance")) {
    body = (
      <>
        <path d="M10 28 V14 l10-4 10 4 V28" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M16 28 V20 H24 V28" stroke="currentColor" strokeWidth="2" fill="none" />
      </>
    );
  }

  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <rect width="40" height="40" rx="12" fill="var(--orange-dim)" />
      <g color="var(--orange)" transform="translate(0,0)">
        {body}
      </g>
    </svg>
  );
}

export function TierVisual({ tier }: { tier: 1 | 2 | 3 }) {
  if (tier === 1) {
    return (
      <svg viewBox="0 0 120 80" className="tier-visual" aria-hidden="true">
        <rect x="16" y="18" width="52" height="48" rx="8" fill="#0B1426" />
        <rect x="26" y="28" width="32" height="4" rx="2" fill="#FF6B00" />
        <rect x="26" y="38" width="24" height="3" rx="1.5" fill="rgba(255,255,255,0.25)" />
        <rect x="26" y="46" width="28" height="3" rx="1.5" fill="rgba(255,255,255,0.15)" />
        <path d="M78 28l18 12-18 12V28z" fill="#FF6B00" />
      </svg>
    );
  }
  if (tier === 2) {
    return (
      <svg viewBox="0 0 120 80" className="tier-visual" aria-hidden="true">
        <rect x="14" y="16" width="40" height="50" rx="8" fill="#0B1426" />
        <rect x="22" y="26" width="24" height="3" rx="1.5" fill="rgba(255,255,255,0.3)" />
        <rect x="22" y="34" width="18" height="3" rx="1.5" fill="rgba(255,255,255,0.18)" />
        <rect x="22" y="42" width="20" height="3" rx="1.5" fill="rgba(255,255,255,0.18)" />
        <path d="M60 40 H78" stroke="#FF6B00" strokeWidth="3" strokeLinecap="round" />
        <circle cx="90" cy="40" r="14" fill="#FF6B00" />
        <path d="M84 40l4 4 8-8" stroke="#fff" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 120 80" className="tier-visual" aria-hidden="true">
      <circle cx="40" cy="40" r="22" fill="#0B1426" />
      <circle cx="40" cy="40" r="10" fill="#FF6B00" />
      <rect x="68" y="22" width="36" height="36" rx="8" fill="#132038" stroke="#FF6B00" strokeWidth="2" />
      <path d="M78 40h16M86 32v16" stroke="#FF6B00" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

export function PatternBand() {
  return (
    <div className="pattern-band" aria-hidden="true">
      <svg width="100%" height="100%" preserveAspectRatio="none" viewBox="0 0 1200 120">
        <defs>
          <pattern id="fib" width="80" height="80" patternUnits="userSpaceOnUse">
            <circle cx="8" cy="8" r="1.2" fill="rgba(255,107,0,0.25)" />
            <path d="M0 40 Q20 20 40 40 T80 40" stroke="rgba(255,107,0,0.12)" fill="none" />
          </pattern>
        </defs>
        <rect width="1200" height="120" fill="url(#fib)" />
      </svg>
    </div>
  );
}
